// Per-tenant suppression of system-generated ticket types that must not
// surface to citizens. Keyed on the resolved CRM hostname
// (useCompany().hostname). Prefix-matched against `ticket_type` — the CRM
// portal v1 API only exposes the type NAME, not an id. Matching is
// trim + case-insensitive so casing/whitespace tweaks on the backend don't
// silently re-expose the tickets; a full rename fails open (tickets reappear).
//
// Provas: "TAG-ID - ikke registreret tømning" tickets are tag-registration
// notices, not failed emptyings — the collection they ride on must render as
// Emptied. The durable fix belongs in the backend (don't mark the collection
// not-emptied / don't expose the ticket type to citizens); this is the
// frontend patch until then.
const SUPPRESSED_TICKET_TYPE_PREFIXES_BY_HOSTNAME: Record<string, string[]> = {
  'provas-portal.wastehero.io': ['TAG-ID'],
};

// API status constants — see collectionStatusApiConstMap in
// src/api/consts/CollectionStatus.tsx
const API_STATUS_COMPLETE = 3;
const API_STATUS_COMPLETE_NOT_EMPTIED = 4;

const getPrefixes = (hostname: string | undefined): string[] =>
  (hostname && SUPPRESSED_TICKET_TYPE_PREFIXES_BY_HOSTNAME[hostname]) || [];

export const hasSuppressedTicketTypes = (
  hostname: string | undefined
): boolean => getPrefixes(hostname).length > 0;

export const isSuppressedTicket = (
  ticket: $TSFixMe,
  hostname: string | undefined
): boolean => {
  const prefixes = getPrefixes(hostname);
  if (!prefixes.length) return false;
  const ticketType = ticket?.ticket_type;
  if (typeof ticketType !== 'string') return false;
  const normalized = ticketType.trim().toLowerCase();
  return prefixes.some((prefix) =>
    normalized.startsWith(prefix.trim().toLowerCase())
  );
};

export const filterSuppressedTickets = (
  tickets: $TSFixMe[],
  hostname: string | undefined
): $TSFixMe[] => {
  if (!tickets?.length || !getPrefixes(hostname).length) return tickets;
  const kept = tickets.filter((t) => !isSuppressedTicket(t, hostname));
  // Preserve identity when nothing matched so memoized consumers don't churn.
  return kept.length === tickets.length ? tickets : kept;
};

/**
 * Strips suppressed tickets from each collection-log entry and, when a
 * "not emptied" status was carried solely by suppressed tickets, overrides the
 * status to Emptied. Collections without matches keep their original
 * reference.
 */
export const normalizeCollections = (
  collections: $TSFixMe[] | undefined,
  hostname: string | undefined
): $TSFixMe[] | undefined => {
  if (!collections?.length || !getPrefixes(hostname).length) {
    return collections;
  }

  let changed = false;
  const normalized = collections.map((collection) => {
    const tickets = collection?.pickup_order?.created_tickets;
    if (!tickets?.length) return collection;

    const kept = tickets.filter(
      (t: $TSFixMe) => !isSuppressedTicket(t, hostname)
    );
    if (kept.length === tickets.length) return collection;

    changed = true;
    const next = {
      ...collection,
      pickup_order: { ...collection.pickup_order, created_tickets: kept },
    };
    if (
      collection.status === API_STATUS_COMPLETE_NOT_EMPTIED &&
      kept.length === 0
    ) {
      next.status = API_STATUS_COMPLETE;
    }
    return next;
  });

  return changed ? normalized : collections;
};
